import { Text, TextSize, TextWeight } from '@vizit/atoms';
import { FormTextField } from '@vizit/forms';
import { InfoCircleIcon } from '@vizit/icons';
import type { Control, FieldValues, Path } from 'react-hook-form';

const PRODUCT_TITLE_HELPER_TEXT =
  'Enter the exact product title, or as much as you know. A more precise title means a more accurate check for agent and mobile readiness.';

interface ProductTitleFieldProps<T extends FieldValues> {
  /** The control object from the parent `useForm` hook. */
  control: Control<T>;
  /** The name of the product-title field in the parent form. */
  name: Path<T>;
}

/**
 * Product title input with an info hint, shown only for hero images.
 * The parent dialog decides whether to render it; this component is presentational.
 */
function ProductTitleField<T extends FieldValues>(props: ProductTitleFieldProps<T>) {
  const { control, name } = props;

  return (
    <div className="flex flex-col gap-6">
      <FormTextField
        control={control}
        name={name}
        label="Product title"
        placeholder="Enter product title"
        isRequired
        className="min-h-0"
        inputProps={{ 'data-testid': 'product-title-input' }}
      />
      <div className="flex items-center gap-6 rounded-none border border-indigo-600 bg-indigo-50 px-6 py-3 shadow-md">
        <span className="shrink-0 text-indigo-600">
          <InfoCircleIcon size={24} />
        </span>
        <Text size={TextSize.Sm} weight={TextWeight.Medium} showEllipsis={false} className="text-quaternary">
          {PRODUCT_TITLE_HELPER_TEXT}
        </Text>
      </div>
    </div>
  );
}

export { ProductTitleField, PRODUCT_TITLE_HELPER_TEXT };
export type { ProductTitleFieldProps };
